import { Injectable } from '@angular/core';
import { VerificationAction, IVerificationContext } from '@core/models';
import { getFriendlyCognitoMessage } from '@core/utils/messages';
import { Observable, of, from, map, catchError } from 'rxjs';
import { CognitoAuthClientService } from './cognito-auth-client.service';

@Injectable({ providedIn: 'root' })
export class VerificationConfirmService {
  private methodMap: Partial<
    Record<VerificationAction, (ctx: IVerificationContext, code: string) => Promise<void>>
  >;

  constructor(private cognitoAuthClient: CognitoAuthClientService) {
    this.methodMap = {
      signUp: async (ctx, code) => {
        if (!ctx.identifier) throw new Error('Identifier required.');
        await this.cognitoAuthClient.confirmSignUp(ctx.identifier, code);
      }
    };
  }

  confirmCode(
    context: IVerificationContext,
    code: string
  ): Observable<{ success: boolean; message: string; type: string }> {
    const handler = this.methodMap[context.actionType];
    if (!handler) {
      return of({ success: false, message: 'Unsupported verification context.', type: 'danger' });
    }

    return from(handler(context, code)).pipe(
      map(() => ({
        success: true,
        message: this.successMessage(context.actionType),
        type: 'success'
      })),
      catchError((error: any) => {
        const friendly = getFriendlyCognitoMessage('confirmCode', error?.code);
        return of({ success: false, message: friendly.message, type: friendly.type });
      })
    );
  }

  private successMessage(action: VerificationAction): string {
    switch (action) {
      case 'signUp':
        return 'Verification successful.';
      case 'login':
      case 'mfa':
        return 'Code confirmed.';
      default:
        return 'Success.';
    }
  }
}
